import { DataSource } from '@angular/cdk/table';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

import { translate } from '@jsverse/transloco';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import { EnumErrorStates, Icons } from '@inmoment/the-kitchen';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { Constants } from 'app/constants/constants';
import {
  DefaultQuickActionIconsEnum,
  DocumentsQuickActionService,
  QuickActionsItem,
} from 'app/modules/collection/services/documents-quick-action.service';
import { FilterService } from 'app/modules/collection/services/filter.service';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { AuthService, Session } from 'app/providers/bridge-api/auth.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { Annotation, AnnotationService } from 'app/providers/storage-api/annotation.service';
import {
  AccountMetadata,
  StorageAccountService,
} from 'app/providers/storage-api/blender-account.service';
import { Collection, CollectionService } from 'app/providers/storage-api/collection.service';
import { Document, DocumentService } from 'app/providers/storage-api/document.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import {
  Project,
  ProjectService,
  SentimentPolarityRange,
} from 'app/providers/storage-api/project.service';
import { encodeParams } from 'app/providers/storage-api/report.service';
import { StorageListResult } from 'app/providers/storage-api.service';
import {
  DownloadTaskPayload,
  DownloadTasksService,
  TaskType,
} from 'app/providers/tasks-api/download.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService, MAT_DIALOG_DATA, MatDialogRef } from 'app/services/dialog.service';
import { LocalStorageKey, LocalStorageService } from 'app/services/local-storage.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import { Utils } from 'app/utils/utils';
import { WidgetDataSource } from '../../../../../models';
import { SemantriaAccount } from '../../../../../providers/bridge-api/account.service';
import { DocumentViewOptions } from '../document-view/document-view.component';
import {
  DownloadAsDialogComponent,
  DownloadAsDialogOptions,
  DownloadAsDialogResult,
} from '../../download-as-dialog/download-as-dialog.component';
import { ChooseTagsOptions } from '../../remove-tags-dialog/remove-tags-dialog.component';

export interface DocumentListOptions {
  collection: Collection;
  query?: string;
  widgetDataSource?: WidgetDataSource;
  sentimentScale?: number;
  sentimentDisplay?: string;
  dashboardId?: string;
  isDashboardLocked?: boolean;
  displayColumns?: string[];
  customItems?: boolean;
  pageSize?: number;
  widgetPayload?: any;
  dataSourceNotFound?: boolean;
}

class DocumentDataSource extends DataSource<any> {
  private documentsSubject = new BehaviorSubject<Document[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(true);

  page: StorageListResult<Document> = {};
  displayedColumns = ['text', 'sentiment'];
  private dataSourceNotFound = false;
  widgetDataSource: WidgetDataSource;
  errorType: string;

  loading$ = this.loadingSubject.asObservable();
  private annotations: StorageListResult<Annotation<any>>;

  constructor(
    private documentService: DocumentService,
    dataSource: WidgetDataSource,
    private annotationService: AnnotationService,
    private loadingFinished: EventEmitter<boolean>,
    private loadingStarted: EventEmitter<boolean>
  ) {
    super();
    this.widgetDataSource = dataSource;
  }

  connect(): Observable<any> {
    this.documentsSubject = new BehaviorSubject<Document[]>([]);
    if (this.page) {
      this.documentsSubject.next(this.page.items);
    }
    return this.documentsSubject.asObservable();
  }

  disconnect(): void {
    this.documentsSubject.complete();
    this.loadingSubject.complete();
  }

  async loadDocuments(
    collectionName: string,
    computedQuery: string,
    limit: number,
    analysisName: string,
    sortField: string,
    sortOrder: string,
    projectId?: string,
    timezone?: string,
    initialLoad?: boolean
  ): Promise<void> {
    this.beforeRequest();
    this.loadingStarted.emit(initialLoad);
    this.errorType = undefined;
    try {
      const [page, annotations] = await Promise.all([
        this.documentService.searchForDocuments(
          this.widgetDataSource,
          projectId,
          collectionName,
          computedQuery,
          limit,
          {
            sortField,
            sortOrder,
            timezone,
          }
        ),
        this.annotationService.getAnnotations(projectId, collectionName),
      ]);

      this.page = page;
      this.annotations = annotations;
      this.page.items = this.getAnnotatedDocumentsSentiment();

      this.documentsSubject.next(this.page.items);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.loadingSubject.next(false);
      this.loadingFinished.emit(true);
    }
  }

  private handleError(e: any) {
    this.errorType = e.error?.type ? e.error.type : 'UnknownError';
    this.page = { items: [], count: 0 };
    this.documentsSubject.next(this.page.items);
  }

  async loadPage(sortField: string, sortOrder: string, timezone?: string, initialLoad?: boolean) {
    const { endpoint, params, headers } = this.page.request;
    const prev = this.page.request.prev;
    params.sortField = sortField;
    params.sortOrder = sortOrder;
    params.timezone = timezone;
    try {
      this.loadingStarted.emit(initialLoad);
      this.beforeRequest();
      this.page = await this.documentService.getPage<Document>(endpoint, params, headers);
      this.page.request.prev = prev;
      this.documentsSubject.next(this.page.items);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.loadingSubject.next(false);
      this.loadingFinished.emit(initialLoad);
    }
  }

  async loadNextPage() {
    try {
      this.beforeRequest();
      this.page = await this.documentService.getNextPage<Document>(this.page);
      this.page.items = this.getAnnotatedDocumentsSentiment();
      this.documentsSubject.next(this.page.items);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.loadingSubject.next(false);
    }
  }

  async loadPrevPage() {
    try {
      this.beforeRequest();
      this.page = await this.documentService.getPrevPage<Document>(this.page);
      this.page.items = this.getAnnotatedDocumentsSentiment();
      this.documentsSubject.next(this.page.items);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.loadingSubject.next(false);
    }
  }

  private beforeRequest() {
    this.loadingSubject.next(true);
    this.dataSourceNotFound = false;
  }

  private getAnnotatedDocumentsSentiment() {
    return this.annotationService.getDocumentSentiments(this.annotations.items, this.page.items);
  }

  get isDataSourceNotFound() {
    return this.dataSourceNotFound;
  }

  set isDataSourceNotFound(dataSourceNotFound) {
    if (dataSourceNotFound) {
      this.loadingSubject.next(false);
    }
    this.dataSourceNotFound = dataSourceNotFound;
  }
}

export interface DocumentListColumnSettings {
  property: string;
  columnId: string;
  label: string;
  display?: boolean;
  sortable?: boolean;
  type?: string;
  pipeParams?: string;
  meta?: boolean;
  available?: boolean;
  inProject: boolean;
  alias?: boolean;
  aliasedFields?: string[];
}

export interface CallbackEvent {
  callbackFn: () => void;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-document-list',
  templateUrl: './document-list.component.html',
  styleUrls: ['./document-list.component.scss'],
  standalone: false,
})
export class DocumentListComponent implements OnInit, OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input()
  get options(): DocumentListOptions {
    return this._options;
  }
  set options(value: DocumentListOptions) {
    this._options = value;
    if (this.options && this.projectId) {
      this.bulkQuickActions = this.getBulkQuickActions(value);
      this.documentQuickActions = this.getDocumentQuickActions(this.bulkQuickActions);
    }
  }

  @Output() refreshDashboard = new EventEmitter<void>();
  @Output() filteredDocumentCountChange = new EventEmitter<number>();
  @Output() loadingStarted = new EventEmitter<boolean>();
  @Output() loadingFinished = new EventEmitter<boolean>();
  @Output() recalculateQuery = new EventEmitter<CallbackEvent>();
  @Output() pageSizeChange = new EventEmitter<number>();

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) set _paginator(paginator: MatPaginator) {
    if (paginator && this.paginator !== paginator) {
      this.paginator = paginator;
      if (this.paginationSubscription) {
        this.paginationSubscription.unsubscribe();
      }
      this.paginationSubscription = this.paginator.page
        .pipe(
          tap((pageEvent) => {
            this.selectAllDocuments(false);
            if (pageEvent.pageSize !== this.limit) {
              this.pageSizeChange.emit(pageEvent.pageSize);
              this.limit = pageEvent.pageSize;
              this.resetDocuments();
            } else {
              this.pageIndex = pageEvent.pageIndex;
              if (pageEvent.pageIndex > pageEvent.previousPageIndex) {
                this.dataSource.loadNextPage();
              } else if (pageEvent.pageIndex === pageEvent.previousPageIndex) {
                this.dataSource.loadPage(this.sortField, this.sortOrder, this.timezone);
              } else {
                this.dataSource.loadPrevPage();
              }
            }
          })
        )
        .subscribe();
    }
  }

  paginator: MatPaginator;

  pageIndex = 0;
  limit = 20;

  projectId: string;
  dataSource: DocumentDataSource;
  filter = '';
  sortProp = '';
  sortField = ''; // empty for default, otherwise elastic field name- analyses.sentimentScore
  sortOrder = ''; // ex. DESC
  projectFields: string[] = [];
  timezone: string;
  paginationSubscription: Subscription;
  collectionResponse: Collection;

  filterHasBeenApplied = false;
  loading = true;
  allSearchDocumentsSelected = false;
  activeUserQuery: string;
  activeUserQueryStatus = { state: EnumErrorStates.SUCCESS, text: '' };

  isTableViewToggled: boolean;

  textFields: DocumentListColumnSettings[] = [
    {
      property: 'text',
      columnId: 'text',
      label: translate('documents.list.text'),
      available: true,
      inProject: true,
    },
    {
      property: 'title',
      columnId: 'title',
      label: translate('documents.list.title'),
      inProject: false,
    },
    {
      property: 'abstract',
      columnId: 'abstract',
      label: translate('documents.list.abstract'),
      inProject: false,
    },
  ];

  otherFields: DocumentListColumnSettings[] = [
    {
      property: 'creator',
      columnId: 'creator',
      label: translate('documents.list.creator'),
      type: 'string',
      available: false,
      inProject: false,
    },
    {
      property: 'indexedAt',
      columnId: 'indexedAt',
      label: translate('documents.list.indexed'),
      type: 'date',
      sortable: false,
      available: true,
      inProject: true,
    },
    {
      property: 'createdAt',
      columnId: 'createdAt',
      label: translate('documents.list.created'),
      type: 'date',
      sortable: true,
      available: false,
      inProject: false,
    },
    {
      property: 'id',
      columnId: 'id',
      label: translate('documents.list.id'),
      type: 'id',
      sortable: true,
      available: true,
      inProject: true,
    },
    {
      property: 'language',
      columnId: 'language',
      label: translate('documents.list.language'),
      type: 'string',
      available: false,
      inProject: false,
    },
    {
      property: 'locationId',
      columnId: 'locationId',
      label: translate('documents.list.location-id'),
      type: 'string',
      available: false,
      inProject: false,
    },
    {
      property: 'parentId',
      columnId: 'parentId',
      label: translate('documents.list.parent'),
      type: 'string',
      available: false,
      inProject: false,
    },
    {
      property: 'publisher',
      columnId: 'publisher',
      label: translate('documents.list.publisher'),
      type: 'string',
      available: false,
      inProject: false,
    },
    {
      property: 'sentiment',
      columnId: 'sentiment',
      label: translate('documents.list.sentiment'),
      type: 'sentiment',
      sortable: true,
      available: true,
      inProject: true,
    },
    {
      property: 'source',
      columnId: 'source',
      label: translate('documents.list.source'),
      type: 'string',
      available: false,
      inProject: false,
    },
    {
      property: 'tags',
      columnId: 'tags',
      label: translate('documents.list.tags'),
      type: 'string',
      available: false,
      inProject: false,
    },
    {
      property: 'type',
      columnId: 'type',
      label: translate('documents.list.type'),
      type: 'string',
      available: false,
      inProject: false,
    },
    {
      property: 'url',
      columnId: 'url',
      label: translate('documents.list.url'),
      type: 'url',
      available: false,
      inProject: false,
    },
  ];

  sentimentRanges: SentimentPolarityRange[];
  project: Project;
  accountMetadata: AccountMetadata;
  selectedDocuments: Document[] = [];

  showDocumentViewModal: boolean;
  documentViewModalOptions: DocumentViewOptions;

  showAddDocumentTagsModal: boolean;
  showRemoveDocumentTagsModal: boolean;
  documentTagsModalOptions: ChooseTagsOptions;
  bulkQuickActions: QuickActionsItem[] = [];
  documentQuickActions: QuickActionsItem[] = [];

  private session: Session;
  private _options: DocumentListOptions;

  constructor(
    private dialogService: DialogService,
    private documentService: DocumentService,
    private accountService: StorageAccountService,
    private projectService: ProjectService,
    private authService: AuthService,
    private alertService: AlertService,
    private downloadTaskService: DownloadTasksService,
    private groupService: GroupService,
    private timezoneService: TimezoneService,
    private localStorageService: LocalStorageService,
    private semantriaAccount: SemantriaAccount,
    private customFieldService: CustomFieldsService,
    private collectionService: CollectionService,
    private documentsQuickActionService: DocumentsQuickActionService,
    private annotationService: AnnotationService,
    @Optional() private dialogRef: MatDialogRef<DocumentListComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogOptions: DocumentListOptions
  ) {}

  async ngOnInit() {
    this.options = this.options || this.dialogOptions;
    this.dataSource = new DocumentDataSource(
      this.documentService,
      this.options.widgetDataSource,
      this.annotationService,
      this.loadingFinished,
      this.loadingStarted
    );
    if (this.options.widgetDataSource?.collectionName) {
      this.collectionResponse = await this.collectionService.getCollectionByName(
        this.options.widgetDataSource.collectionName,
        this.options.collection.projectId,
        this.options.widgetDataSource.isSocial
      );
    }
    this.limit = this.options.pageSize || this.limit;
    this.projectId = this.options.collection.projectId;
    this.activeUserQuery = this.options.widgetPayload?.userQuery || '';
    const [project, accountMetadata, session] = await Promise.all([
      this.projectService.getProjectById(this.projectId, false),
      this.accountService.groupFieldsSub.latest(),
      this.authService.getSession(),
    ]);
    this.project = project;
    this.sentimentRanges = this.project.polarities.document[this.options.sentimentScale];
    this.accountMetadata = accountMetadata;
    this.session = session;
    this.bulkQuickActions = this.getBulkQuickActions(this.options);
    this.documentQuickActions = this.getDocumentQuickActions(this.bulkQuickActions);

    this.loadingFinished.subscribe((finish) => {
      if (finish) {
        this.loading = false;
      }
    });

    await this.refresh(true, undefined, true);
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.options && changes.options.currentValue && this.project) {
      this.sentimentRanges = this.project.polarities.document[this.options.sentimentScale];
      if (changes.options.currentValue.widgetDataSource) {
        await this.configureAvailableFields(changes.options.currentValue.widgetDataSource);
      }
    }
  }

  ngOnDestroy() {
    if (this.paginationSubscription) {
      this.paginationSubscription.unsubscribe();
    }
  }

  async refresh(
    reset = false,
    updatedQuery?: string,
    initialLoad = false,
    refreshedDatasource?: WidgetDataSource
  ) {
    function setDisplayProperty(fieldsArray, payloadColumns) {
      fieldsArray
        .filter((field) => payloadColumns.includes(field.columnId))
        .forEach((field) => (field.display = true));
    }

    this.timezone = this.timezoneService.timezone;

    if (reset) {
      this.options.widgetDataSource = refreshedDatasource || this.options.widgetDataSource;
      if (updatedQuery) {
        this.options.query = updatedQuery;
      }
      this.dataSource.widgetDataSource = this.options.widgetDataSource;
      try {
        this.sortProp = 'id';
        this.sortField = 'publicId';
        this.sortOrder = 'ASC';
        await this.resetDocuments(initialLoad);
      } catch (error) {
        this.dataSource.isDataSourceNotFound = true;
      }
    } else {
      this.dataSource.loadPage(this.sortField, this.sortOrder, this.timezone, initialLoad);
    }

    await this.configureAvailableFields();

    if (this.options.displayColumns) {
      setDisplayProperty(this.textFields, this.options.displayColumns);
      setDisplayProperty(this.otherFields, this.options.displayColumns);
    } else {
      const availableTextFields = ['text'];
      if (this.textFields.find((f) => f.columnId === 'title').available) {
        availableTextFields.push('title');
      }

      setDisplayProperty(this.textFields, availableTextFields);
      setDisplayProperty(this.otherFields, ['sentiment']);
    }

    this.setDisplayColumns(this.textFields, this.otherFields, true);
  }

  async configureAvailableFields(widgetDataSource?) {
    const projectFields: string[] = await this.projectService.getProjectFields(this.projectId);

    const metadataFields = this.options.widgetDataSource
      ? await this.customFieldService.getMetadataSubTypesAsync(this.options.widgetDataSource)
      : [];

    this.projectFields = Utils.mergeArrays(
      projectFields.filter((f) => !FieldService.isCustomField(f)),
      metadataFields
    );

    this.accountMetadata.metadata
      .filter((f) => !f.metadata || f.metadata.display !== false)
      .forEach((f) => {
        if (
          this.projectFields.indexOf(f.fieldName) > -1 &&
          f.fieldName !== 'tags' &&
          this.otherFields.findIndex((of) => of.property === f.fieldName) === -1 &&
          (f.metadata?.redacted !== true || !this.authService.isAlwaysRedact)
        ) {
          this.otherFields.push({
            property: f.fieldName,
            columnId: `metadata.${f.fieldName}`,
            label: f.description,
            type: f.fieldType,
            meta: true,
            display: false,
            available: true,
            inProject: true,
            alias: f.alias,
            aliasedFields: f.aliasedFields,
          });
        }
      });
    this.otherFields.sort((f1, f2) =>
      f1.label.localeCompare(f2.label, undefined, { sensitivity: 'accent' })
    );

    if (this.options.displayColumns) {
      this.otherFields.sort(
        (f1, f2) =>
          this.options.displayColumns.indexOf(f1.columnId) -
          this.options.displayColumns.indexOf(f2.columnId)
      );
    }

    this.textFields
      .filter((f) => f.columnId !== 'text')
      .forEach((f) => {
        f.available = false;
        f.inProject = false;
      });
    this.otherFields
      .filter(
        (f) => f.columnId !== 'sentiment' && f.columnId !== 'id' && f.columnId !== 'indexedAt'
      )
      .forEach((f) => {
        f.available = false;
        f.inProject = false;
      });
    let dsFields = [];
    if (this.dataSource && widgetDataSource) {
      this.dataSource.widgetDataSource = widgetDataSource;
    }
    if (this.dataSource && this.dataSource.widgetDataSource) {
      dsFields = dsFields.concat(this.dataSource.widgetDataSource.standardFields);
      dsFields = dsFields.concat(metadataFields);
      dsFields.push('indexedAt');
      if (this.dataSource.widgetDataSource.hasDateData) {
        dsFields.push('createdAt');
      }
    }
    this.textFields.forEach((f) => {
      if (this.projectFields.indexOf(f.property) > -1) {
        f.inProject = true;
      }
      if (dsFields.indexOf(f.property) > -1) {
        f.available = true;
      }
    });
    this.otherFields.forEach((f) => {
      if (this.projectFields.indexOf(f.property) > -1) {
        f.inProject = true;
      }
      if (dsFields.indexOf(f.property) > -1) {
        f.available = true;
      }
    });
  }

  get userQuery(): string {
    return this.options.widgetPayload?.userQuery || '';
  }

  set userQuery(query: string) {
    if (!this.options.widgetPayload) {
      this.options.widgetPayload = { userQuery: '' };
    }
    this.options.widgetPayload.userQuery = query;
  }

  private getBulkQuickActions(options: DocumentListOptions): QuickActionsItem[] {
    if (this.authService.isReadOnly || this.options.isDashboardLocked) {
      return null;
    }
    // For some reason documentViewer was/is undefined optional chaining returning undefined
    const enabled =
      options?.widgetPayload?.documentViewer?.quickActions?.filter((q) => q.enabled) ??
      this.documentsQuickActionService.defaultQuickActions;

    const showQuickActions = options?.widgetPayload?.documentViewer?.showQuickActions;

    /*
      showQuickActions boolean value means it's configured;
      if showQuickActions is null or undefined, then display defaultQuickActions since config is not yet performed
    */
    return showQuickActions !== false ? this.mapBulkQuickActionIcons(enabled) : null;
  }

  private mapBulkQuickActionIcons(actions: QuickActionsItem[]): QuickActionsItem[] {
    return actions?.map((action) => {
      if (
        action.icon === DefaultQuickActionIconsEnum.TAG ||
        action.icon === DefaultQuickActionIconsEnum.TAG_FILLED ||
        action.icon === DefaultQuickActionIconsEnum.TRASH ||
        this.selectedDocuments.length === 0
      ) {
        return action;
      }

      const containsTag = this.selectedDocuments.every((doc) =>
        doc.tags.some((tag) => tag?.trim()?.toLowerCase() === action.tag?.trim()?.toLowerCase())
      );

      let icon = action.icon.split('_filled')[0] as Icons;
      let title = action.title;
      if (containsTag) {
        icon = `${action.icon}_filled` as Icons;
        title = translate('documents.list-settings.quick-actions.clear-title', {
          title: action.title,
        });
      }

      return {
        ...action,
        icon,
        title,
      };
    });
  }

  private getDocumentQuickActions(actions: QuickActionsItem[]): QuickActionsItem[] {
    if (this.authService.isReadOnly || this.options.isDashboardLocked) {
      return null;
    }
    return actions?.map((action) => ({
      ...action,
      tagFunction: (document: Document) => this.tagFromCardView(document),
      untagFunction: (document: Document) => this.untagFromCardView(document),
      deleteFunction: (document: Document) => this.deleteFromCardView(document),
      cardViewTagSelection: ($event: {
        action: 'ADD' | 'REMOVE';
        tags: string[];
        document: Document;
      }) => this.onSingleTagSelection($event),
    }));
  }

  private async getActionFunction(action: QuickActionsItem) {
    switch (action.icon) {
      case DefaultQuickActionIconsEnum.TRASH:
        return this.deleteSelectedDocumentsClicked();
      case DefaultQuickActionIconsEnum.TAG:
        return this.tagSelectedDocumentsClicked();
      case DefaultQuickActionIconsEnum.TAG_FILLED:
        return this.untagSelectedDocumentsClicked();
      default:
        await this.tagSelectedDocumentsAction({
          action: action.icon?.endsWith('_filled') ? 'REMOVE' : 'ADD',
          tags: [action.tag],
        });
        return;
    }
  }

  private async resetDocuments(initialLoad = false) {
    if (this.options.dataSourceNotFound && !this.options.widgetDataSource) {
      this.dataSource.isDataSourceNotFound = true;
      this.loadingFinished.emit(initialLoad);
      return;
    }

    this.loading = true;
    this.dataSource.page = {};

    await new Promise<void>((resolve) => this.recalculateQuery.emit({ callbackFn: resolve }));
    await this.loadDocuments(initialLoad);
  }

  onSearchDocumentsByQuery() {
    this.userQuery = this.activeUserQuery;
    this.options.query = this.activeUserQuery;
    const sessionStorageKey = `${LocalStorageKey.SESSION_USER_DOCUMENT_SEARCHES}_${this.options.dashboardId}`;
    if (this.activeUserQuery.trim().length > 0) {
      const sessionStorageSearches =
        this.localStorageService.getItem(sessionStorageKey, 'session') || [];
      sessionStorageSearches.push(this.activeUserQuery);
      this.localStorageService.setItem(
        sessionStorageKey,
        [...new Set(sessionStorageSearches)],
        'session'
      );
    }

    this.resetDocuments();
  }

  clearDocumentSearch() {
    this.activeUserQuery = '';
    this.options.query = this.activeUserQuery;
    this.activeUserQueryStatus = { state: EnumErrorStates.SUCCESS, text: '' };
    this.resetDocuments();
  }

  async loadDocuments(initialLoad = false) {
    this.loading = true;
    this.activeUserQueryStatus = { state: EnumErrorStates.SUCCESS, text: '' };
    this.selectedDocuments = [];
    this.allSearchDocumentsSelected = false;
    this.filterHasBeenApplied = false;
    if (this.options.widgetPayload) {
      const hasUserQuery = !!this.options.widgetPayload.userQuery;
      const hasFilters = FilterService.analyticItemFilterSetsHasFilters(
        this.options.widgetPayload.filters,
        this.options.widgetPayload.userSearch
      );
      this.filterHasBeenApplied = hasUserQuery || hasFilters;
    }
    if (this.dataSource.page.request) {
      await this.dataSource.loadPage(this.sortField, this.sortOrder, this.timezone, initialLoad);
    } else {
      await this.dataSource.loadDocuments(
        this.collectionName,
        this.computedQuery,
        this.limit,
        this.analysisName,
        this.sortField,
        this.sortOrder,
        this.projectId,
        this.timezone,
        initialLoad
      );

      if (this.paginator) {
        this.paginator.pageIndex = 0;
        this.pageIndex = 0;
      }
    }

    if (this.dataSource.errorType) {
      let text = this.dataSource.errorType;
      switch (this.dataSource.errorType) {
        case 'QuerySyntaxInvalid':
          text = translate('documents.list.error-search-syntax');
          break;
        case 'ElasticRestStatusException':
          text = translate('documents.list.error-search-syntax');
      }
      this.activeUserQueryStatus = { state: EnumErrorStates.ERROR, text };
    }
    this.filteredDocumentCountChange.emit(this.dataSource.page.count);
  }

  documentText(document) {
    const analysis = document.analyses[0];
    return analysis.redactedText || document.text || Constants.BLANK;
  }

  get showSelectAll(): boolean {
    const pageCount =
      this.loading || this.dataSource.isDataSourceNotFound ? 0 : this.dataSource.page.items?.length;
    const docCount =
      this.loading || this.dataSource.isDataSourceNotFound ? 0 : this.dataSource.page.count;
    return (
      this.showSelectCount &&
      this.filterHasBeenApplied &&
      this.selectedDocuments.length === pageCount &&
      this.selectedDocuments.length !== docCount
    );
  }

  get showSelectCount(): boolean {
    const docCount =
      this.loading || this.dataSource.isDataSourceNotFound ? 0 : this.dataSource.page.count;
    return (
      !this.authService.isReadOnly &&
      !this.allSearchDocumentsSelected &&
      this.selectedDocuments.length <= docCount &&
      this.selectedDocuments.length > 0
    );
  }

  get showDocumentActions(): boolean {
    return this.selectedDocuments.length > 0;
  }

  get pageCount(): number {
    return this.loading || this.dataSource?.isDataSourceNotFound
      ? 0
      : this.dataSource?.page?.items?.length;
  }

  get exsistingTags(): string[] {
    const tags: string[] = [];
    for (let i = 0; i < this.selectedDocuments.length; i++) {
      tags.push(...this.selectedDocuments[i].tags);
    }

    return [...new Set(tags)];
  }

  selectAllInSearch() {
    this.allSearchDocumentsSelected = true;
  }

  isSelectedDocument(document: Document): boolean {
    return this.selectedDocuments.some(
      (d) => d.id === document.id && d.collectionName === document.collectionName
    );
  }

  selectDocument(document: Document, checked: boolean) {
    const currentlySelected = this.isSelectedDocument(document);
    if (!checked) {
      this.allSearchDocumentsSelected = false;
    }
    if (checked && !currentlySelected) {
      this.selectedDocuments = this.selectedDocuments.concat(document); // use concat to reinitialize selectedDocuments
    } else if (!checked && currentlySelected) {
      this.selectedDocuments = this.selectedDocuments.filter(
        (d) => d.id !== document.id || d.collectionName !== document.collectionName
      );
    }
    this.bulkQuickActions = this.getBulkQuickActions(this.options);
  }

  selectAllDocuments(checked: boolean) {
    if (this.loading) {
      return;
    }
    this.selectedDocuments = checked ? [...this.dataSource.page.items] : [];
    this.allSearchDocumentsSelected = false;
    this.bulkQuickActions = this.getBulkQuickActions(this.options);
  }

  deleteSelectedDocumentsClicked() {
    const content = this.allSearchDocumentsSelected
      ? translate('documents.list.delete-all-matching', { count: this.dataSource.page.count })
      : translate('documents.list.delete-selected', { count: this.selectedDocuments.length });
    this.dialogService
      .showWarningPopup({
        title: translate('documents.list.delete-title'),
        content,
        confirmTitle: translate('documents.list.delete'),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }
        this.tagSelectedDocumentsAction({ action: 'ADD', tags: ['deleted'] });
      });
  }

  tagSelectedDocumentsClicked() {
    this.documentTagsModalOptions = {
      content: this.allSearchDocumentsSelected
        ? translate('documents.list.tag-all-matching', { count: this.dataSource.page.count })
        : translate('documents.list.tag-selected', { count: this.selectedDocuments.length }),
      placeholder: translate('documents.list.add-tag'),
    };
    this.showAddDocumentTagsModal = true;
  }

  untagSelectedDocumentsClicked() {
    this.documentTagsModalOptions = {
      content: this.allSearchDocumentsSelected
        ? translate('documents.list.remove-tag-all-matching', { count: this.dataSource.page.count })
        : translate('documents.list.remove-tag-selected', { count: this.selectedDocuments.length }),
      placeholder: translate('documents.list.tags-to-remove'),
      existingTags: this.exsistingTags,
      buttonText: translate('documents.list.remove-tags'),
    };
    this.showRemoveDocumentTagsModal = true;
  }

  onQuickActionClick(action: QuickActionsItem): void {
    this.getActionFunction(action);
  }

  untagFromCardView(document: Document): void {
    this.selectedDocuments = [document];
    this.untagSelectedDocumentsClicked();
  }

  tagFromCardView(document: Document): void {
    this.selectedDocuments = [document];
    this.tagSelectedDocumentsClicked();
  }

  deleteFromCardView(document: Document): void {
    this.selectedDocuments = [document];
    this.deleteSelectedDocumentsClicked();
  }

  onSingleTagSelection($event: { action: 'ADD' | 'REMOVE'; tags: string[]; document: Document }) {
    this.tagSelectedDocumentsAction($event, [$event.document]);
  }

  async tagSelectedDocumentsAction(
    $event: { action: 'ADD' | 'REMOVE'; tags?: string[] },
    doc?: Document[]
  ) {
    this.showAddDocumentTagsModal = false;
    this.showRemoveDocumentTagsModal = false;

    if (!$event?.tags) {
      return;
    }

    const selected = doc ?? this.selectedDocuments;

    const patchDocsWithTags = async (api, source: string, documentsSelector: string | string[]) => {
      const response = await api(
        source,
        documentsSelector,
        $event.tags,
        $event.action === 'REMOVE',
        this.projectId
      );
      return response.updated;
    };

    let count = 0;
    if (this.dataSource.widgetDataSource.groupId && this.allSearchDocumentsSelected) {
      count += await patchDocsWithTags(
        this.documentService.tagDocumentsByQueryAg.bind(this.documentService),
        this.dataSource.widgetDataSource.groupId,
        this.computedQuery
      );
    } else {
      const documentsByCollections = selected.reduce((prev, curr) => {
        prev[curr.collectionName] = prev[curr.collectionName] || [];
        prev[curr.collectionName].push(curr);
        return prev;
      }, {});

      for (const collectionName of Object.keys(documentsByCollections)) {
        if (this.allSearchDocumentsSelected) {
          count += await patchDocsWithTags(
            this.documentService.tagDocumentsByQuery.bind(this.documentService),
            collectionName,
            this.computedQuery
          );
        } else {
          count += await patchDocsWithTags(
            this.documentService.tagDocumentsByDocIds.bind(this.documentService),
            collectionName,
            documentsByCollections[collectionName].map((d) => d.id)
          );
        }
      }
    }
    this.alertService.showToast(translate('documents.list.update-success', { count }));
    await this.resetDocuments();
  }

  async downloadAsExcel(filteredDocumentCount: number) {
    const account = await this.semantriaAccount.sub.latest();
    const exportSizeLimit = account.storageLimits['EXPORT_MAX_SIZE_EXCEL'] || 15000;
    this.dialogService
      .showDialog<DownloadAsDialogComponent, DownloadAsDialogOptions>(
        DownloadAsDialogComponent,
        {
          recordCount: Math.min(
            filteredDocumentCount || this.dataSource.widgetDataSource.documentCount,
            exportSizeLimit
          ),
          sort: true,
          format: 'xlsx',
        },
        { width: '600px' },
        false
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const downloadAsResult = result as DownloadAsDialogResult;
          let sortField = downloadAsResult.sortOrder;
          let sortOrder: string;
          if (sortField.indexOf(':') !== -1) {
            const ary = sortField.split(':');
            sortField = ary[0];
            sortOrder = ary[1];
          }

          this.createDownloadTask(
            'EXCEL',
            downloadAsResult.fileName,
            sortField,
            sortOrder,
            downloadAsResult.recordCount
          );
        }
      });
  }

  downloadAsCsv() {
    this.createDownloadTask('CSV', 'documents', 'createdAt', 'DESC');
  }

  private createDownloadTask(
    format: 'EXCEL' | 'CSV',
    filename: string,
    sortField: string,
    sortOrder: string,
    size?: number
  ) {
    const taskPayload: DownloadTaskPayload = {
      _type: TaskType.EXPORT_DOCUMENTS,
      groupId: this.groupService.getSelectedGroupId(true),
      format,
      projectId: this.projectId,
      filename,
      source: 'Widget',
      query: this.computedQuery,
      params: encodeParams(
        {
          analysisName: this.dataSource.widgetDataSource.analysisId,
          timezone: this.timezoneService.timezone,
        },
        this.dataSource.widgetDataSource.groupId
      ),
      sortField,
      sortOrder,
      collectionId: this.collectionName,
      analysisId: this.dataSource.widgetDataSource.analysisId,
      analysisGroupId: this.dataSource.widgetDataSource.groupId,
    };

    if (size) {
      taskPayload.size = size;
    }

    this.downloadTaskService
      .createTask(taskPayload)
      .then(() => {
        const msg = translate('documents.list.download-started');
        this.alertService.showToast(
          msg,
          0,
          'default',
          false,
          'open-download-center-pointer',
          'open-download-center'
        );
      })
      .catch((error) =>
        this.alertService.showErrorToast(translate('documents.list.download-failed'), error)
      );
  }

  get computedQuery(): string {
    return this.options.query || this.userQuery;
  }

  get collection(): Collection | { collectionName: string } {
    return this.options.collection;
  }

  async onSort($event: { active: 'id' | 'sentiment' | 'createdAt'; direction: 'asc' | 'desc' }) {
    this.sortProp = $event.active;
    switch ($event.active) {
      case 'id':
        this.sortField = 'publicId';
        break;
      case 'sentiment':
        this.sortField = 'analyses.sentimentScore';
        break;
      case 'createdAt':
        this.sortField = 'createdAt';
        break;
    }
    this.sortOrder = $event.direction.toUpperCase();
    this.resetDocuments();
  }

  async openDocument(document: Document) {
    if (this.options.widgetPayload?.isClickOpenDocument === false) {
      return;
    }

    const project = await this.projectService.getProjectById(this.projectId, false);

    let analysisName = this.options.widgetDataSource
      ? this.options.widgetDataSource.analysisId
      : undefined;
    if (document.analyses) {
      analysisName = document.analyses[0].analysisName;
    }

    this.documentViewModalOptions = {
      collectionName: document.collectionName,
      page: this.dataSource.page,
      pageIndex: this.pageIndex,
      documentIndex: this.dataSource.page.items.indexOf(document),
      limit: this.limit,
      documentId: document.id,
      analysisName,
      projectId: this.projectId || null,
      sentimentScale: this.options.sentimentScale,
      sentimentDisplay: this.options.sentimentDisplay,
      dashboardId: this.options.dashboardId,
      sentimentRanges: {
        feature: project.polarities.feature[this.options.sentimentScale],
        document: project.polarities.document[this.options.sentimentScale],
      },
      isDashboardLocked: this.options.isDashboardLocked,
      showNlpData: this.options.widgetPayload?.documentViewer?.showNlpData,
      nlpFields: this.options.widgetPayload?.documentViewer?.nlpFields,
      showMetadata: this.options.widgetPayload?.documentViewer?.showMetadata,
      metadataFields: this.options.widgetPayload?.documentViewer?.metadataFields,
      widgetDataSource: this.options.widgetDataSource,
      showQuickActions: this.options.widgetPayload?.documentViewer?.showQuickActions,
      quickActions: this.options.widgetPayload?.documentViewer?.quickActions,
    };
    this.showDocumentViewModal = true;
  }

  onDocumentViewModalClosed(result?) {
    this.showDocumentViewModal = false;
    if (result) {
      this.refreshDashboard.emit();
    }
  }

  isTextfieldDisplayed(prop) {
    return this.textFields.find((field) => field.property === prop)?.display;
  }

  setDisplayColumns(
    textFields = this.textFields,
    standardFields = this.otherFields,
    initialLoad = false
  ) {
    this.textFields = textFields;
    this.otherFields = standardFields;

    if (!initialLoad) {
      this.loadingStarted.emit();
    }

    const displayColumns = this.otherFields
      .filter((field) => field.display)
      .map((field) => field.columnId);
    const textColumnDisplayed = this.textFields.some((field) => field.display);

    if (textColumnDisplayed) {
      displayColumns.unshift('text');
    }

    if (!this.authService.isReadOnly && !this.options.isDashboardLocked) {
      displayColumns.unshift('docSelect');
    }

    if (
      this.dataSource.displayedColumns.indexOf('docSelect') !== displayColumns.indexOf('docSelect')
    ) {
      this.selectAllDocuments(false);
    }

    this.dataSource.displayedColumns = displayColumns;

    if (!initialLoad) {
      this.loadingFinished.emit();
    }
  }

  getCellContent(coldef: DocumentListColumnSettings, document: Document): string[] {
    if (!coldef.meta) {
      return [document[coldef.property]];
    }

    if (coldef.alias) {
      return coldef.aliasedFields.map((f) => document.metadata[f]);
    }

    return [document.metadata[coldef.property]];
  }

  private get collectionName(): string {
    return this.options.widgetDataSource
      ? this.options.widgetDataSource.collectionName
      : this.collection.collectionName;
  }

  private get analysisName(): string {
    return this.options.widgetDataSource ? this.options.widgetDataSource.analysisId : undefined;
  }

  columnLabelFor(coldef) {
    return this.options.widgetPayload?.columnDisplayLabels?.[coldef.columnId] ?? coldef.label;
  }

  getTextColumn(columnId) {
    return this.textFields.find((f) => f.columnId === columnId);
  }
}
